import type { FrontKind, FurniturePart, Vec2 } from "../types";
import {
  boundingBox,
  differenceOutline,
  intersectionOutline,
  polygonArea,
  unionOutline,
} from "../geom";
import { clamp, ellipse, part, rect, resetPartIds, divide } from "./geometry";

/**
 * Shape-building operations for the free-form furniture creator: primitive
 * stamps with boolean union / cut, baked corner rounding and edge bending,
 * polygon insetting, and the "parts step" that turns a bare outline into
 * seat / back / arm / top parts so materials render correctly on any shape.
 *
 * Everything here is pure and works in inches.
 */

export type PrimitiveKind = "rect" | "rounded" | "circle" | "ellipse" | "quarter";

export const PRIMITIVES: { id: PrimitiveKind; label: string }[] = [
  { id: "rect", label: "Rectangle" },
  { id: "rounded", label: "Rounded" },
  { id: "circle", label: "Circle" },
  { id: "ellipse", label: "Ellipse" },
  { id: "quarter", label: "Quarter round" },
];

/** Outline of a primitive centered at `(cx, cy)` with size `w × h`. */
export function primitiveOutline(kind: PrimitiveKind, cx: number, cy: number, w: number, h: number): Vec2[] {
  switch (kind) {
    case "rect":
      return rect(cx - w / 2, cy - h / 2, w, h);
    case "rounded": {
      const r = clamp(Math.min(w, h) * 0.25, 1, 12);
      return roundAllCorners(rect(cx - w / 2, cy - h / 2, w, h), r);
    }
    case "circle": {
      const r = Math.min(w, h) / 2;
      return ellipse(cx, cy, r, r);
    }
    case "ellipse":
      return ellipse(cx, cy, w / 2, h / 2);
    case "quarter": {
      // Quarter disc with its right angle at the top-left of the box.
      const out: Vec2[] = [{ x: cx - w / 2, y: cy - h / 2 }];
      const segs = 24;
      for (let i = 0; i <= segs; i++) {
        const t = (i / segs) * (Math.PI / 2);
        out.push({ x: cx - w / 2 + Math.cos(t) * w, y: cy - h / 2 + Math.sin(t) * h });
      }
      return out;
    }
  }
}

export interface ShapeOpResult {
  points: Vec2[];
  ok: boolean;
  /** Human-readable reason when `ok` is false. */
  message?: string;
}

/** Union a primitive into the outline (or start from it when empty). A
 *  shape that doesn't touch the existing outline is rejected — outlines
 *  must stay one connected piece. */
export function addShape(outline: Vec2[], shape: Vec2[]): ShapeOpResult {
  if (outline.length < 3) return { points: shape, ok: true };
  const merged = unionOutline([outline, shape]);
  if (!merged) return { points: outline, ok: false, message: "Couldn't merge that shape." };
  const before = Math.abs(polygonArea(outline));
  const after = Math.abs(polygonArea(merged));
  // Disjoint union keeps only the larger piece → area unchanged.
  if (after <= before + 1e-6) {
    return { points: outline, ok: false, message: "Overlap the outline so the shapes join." };
  }
  return { points: merged, ok: true };
}

/** Cut a primitive out of the outline. Refuses to cut everything away, and
 *  rejects cuts that would only punch a hole (outlines can't have holes —
 *  the cut has to reach an edge). */
export function cutShape(outline: Vec2[], shape: Vec2[]): ShapeOpResult {
  if (outline.length < 3) return { points: outline, ok: false, message: "Draw or add a shape first." };
  const diff = differenceOutline(outline, shape);
  if (!diff || diff.length < 3 || Math.abs(polygonArea(diff)) < 4) {
    return { points: outline, ok: false, message: "That would cut the whole shape away." };
  }
  const before = Math.abs(polygonArea(outline));
  const after = Math.abs(polygonArea(diff));
  if (after >= before - 1e-6) {
    return { points: outline, ok: false, message: "Cuts need to reach an edge — outlines can't have holes." };
  }
  return { points: diff, ok: true };
}

/** Replace one vertex with an arc of `radius` (baked into the polygon). */
export function roundVertex(points: Vec2[], index: number, radius: number, segs = 8): Vec2[] {
  const n = points.length;
  if (n < 3 || radius <= 0) return points;
  const prev = points[(index - 1 + n) % n];
  const cur = points[index];
  const next = points[(index + 1) % n];
  const d1 = Math.hypot(cur.x - prev.x, cur.y - prev.y);
  const d2 = Math.hypot(next.x - cur.x, next.y - cur.y);
  const r = Math.min(radius, d1 / 2, d2 / 2);
  if (r <= 1e-6) return points;
  const inX = cur.x - ((cur.x - prev.x) / d1) * r;
  const inY = cur.y - ((cur.y - prev.y) / d1) * r;
  const outX = cur.x + ((next.x - cur.x) / d2) * r;
  const outY = cur.y + ((next.y - cur.y) / d2) * r;
  // Sample the quadratic Bézier (in → cur → out).
  const arc: Vec2[] = [];
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    const mt = 1 - t;
    arc.push({
      x: mt * mt * inX + 2 * mt * t * cur.x + t * t * outX,
      y: mt * mt * inY + 2 * mt * t * cur.y + t * t * outY,
    });
  }
  return [...points.slice(0, index), ...arc, ...points.slice(index + 1)];
}

export function roundAllCorners(points: Vec2[], radius: number): Vec2[] {
  // Round from the last vertex backwards so indices stay valid.
  let out = points;
  for (let i = points.length - 1; i >= 0; i--) {
    out = roundVertex(out, i, radius, 6);
  }
  return out;
}

/**
 * Replace edge `edgeIndex` with a quadratic curve bulging `bulge` inches
 * outward (negative = inward). Baked into the polygon.
 */
export function bendEdge(points: Vec2[], edgeIndex: number, bulge: number, segs = 12): Vec2[] {
  const n = points.length;
  if (n < 3 || Math.abs(bulge) < 1e-6) return points;
  const a = points[edgeIndex];
  const b = points[(edgeIndex + 1) % n];
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  // Outward normal: pick the perpendicular pointing away from the centroid.
  let nx = -(b.y - a.y) / len;
  let ny = (b.x - a.x) / len;
  const c = centroid(points);
  if ((mx + nx - c.x) ** 2 + (my + ny - c.y) ** 2 < (mx - nx - c.x) ** 2 + (my - ny - c.y) ** 2) {
    nx = -nx;
    ny = -ny;
  }
  // A quadratic curve's midpoint sits halfway to the control point, so
  // double the bulge for the control.
  const cx = mx + nx * bulge * 2;
  const cy = my + ny * bulge * 2;
  const curve: Vec2[] = [];
  for (let i = 1; i < segs; i++) {
    const t = i / segs;
    const mt = 1 - t;
    curve.push({
      x: mt * mt * a.x + 2 * mt * t * cx + t * t * b.x,
      y: mt * mt * a.y + 2 * mt * t * cy + t * t * b.y,
    });
  }
  return [...points.slice(0, edgeIndex + 1), ...curve, ...points.slice(edgeIndex + 1)];
}

function centroid(points: Vec2[]): Vec2 {
  let x = 0;
  let y = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
  }
  return { x: x / points.length, y: y / points.length };
}

/**
 * Inset (shrink) a polygon by `d` inches. Each edge is offset inward and
 * consecutive offset lines are intersected. Robust for convex and mildly
 * concave outlines with `d` well below the smallest feature size; returns
 * `null` if the result collapses.
 */
export function insetPolygon(points: Vec2[], d: number): Vec2[] | null {
  const n = points.length;
  if (n < 3) return null;
  if (d <= 0) return points.slice();
  const positive = polygonArea(points) > 0;
  // Inward normal for each edge. With the shoelace sign convention used by
  // `polygonArea`, a positive-area polygon has its *left* normal
  // (-dy, dx) pointing inward; negative-area polygons need the flip.
  const offsetEdges: { a: Vec2; b: Vec2 }[] = [];
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    let nx = -(b.y - a.y) / len;
    let ny = (b.x - a.x) / len;
    if (!positive) {
      nx = -nx;
      ny = -ny;
    }
    offsetEdges.push({
      a: { x: a.x + nx * d, y: a.y + ny * d },
      b: { x: b.x + nx * d, y: b.y + ny * d },
    });
  }
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const e1 = offsetEdges[(i - 1 + n) % n];
    const e2 = offsetEdges[i];
    const p = lineIntersection(e1.a, e1.b, e2.a, e2.b) ?? e2.a;
    out.push(p);
  }
  const areaBefore = Math.abs(polygonArea(points));
  const areaAfter = Math.abs(polygonArea(out));
  if (!Number.isFinite(areaAfter) || areaAfter < 1 || areaAfter > areaBefore) return null;
  // Keep the same winding as the input — if the inset flipped, it collapsed.
  if (polygonArea(out) > 0 !== positive) return null;
  // Every inset edge must still run the same way as its source edge; a
  // reversed edge means two offset lines crossed (the shape is too thin).
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const a2 = out[i];
    const b2 = out[(i + 1) % n];
    const dot = (b.x - a.x) * (b2.x - a2.x) + (b.y - a.y) * (b2.y - a2.y);
    if (dot <= 0) return null;
  }
  return out;
}

function lineIntersection(a1: Vec2, a2: Vec2, b1: Vec2, b2: Vec2): Vec2 | null {
  const d = (a1.x - a2.x) * (b1.y - b2.y) - (a1.y - a2.y) * (b1.x - b2.x);
  if (Math.abs(d) < 1e-9) return null;
  const t = ((a1.x - b1.x) * (b1.y - b2.y) - (a1.y - b1.y) * (b1.x - b2.x)) / d;
  return { x: a1.x + t * (a2.x - a1.x), y: a1.y + t * (a2.y - a1.y) };
}

/** A band `depth` inches deep along edge `edgeIndex`, clipped to the outline. */
export function edgeBand(points: Vec2[], edgeIndex: number, depth: number): Vec2[] | null {
  const n = points.length;
  if (n < 3) return null;
  const a = points[edgeIndex];
  const b = points[(edgeIndex + 1) % n];
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  let nx = -(b.y - a.y) / len;
  let ny = (b.x - a.x) / len;
  const c = centroid(points);
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  // Point the normal inward (toward the centroid).
  if ((mx + nx - c.x) ** 2 + (my + ny - c.y) ** 2 > (mx - nx - c.x) ** 2 + (my - ny - c.y) ** 2) {
    nx = -nx;
    ny = -ny;
  }
  // Extend the strip a little past the edge ends so it reaches into corners.
  const ex = ((b.x - a.x) / len) * 0.5;
  const ey = ((b.y - a.y) / len) * 0.5;
  const strip: Vec2[] = [
    { x: a.x - ex, y: a.y - ey },
    { x: b.x + ex, y: b.y + ey },
    { x: b.x + ex + nx * depth, y: b.y + ey + ny * depth },
    { x: a.x - ex + nx * depth, y: a.y - ey + ny * depth },
  ];
  return intersectionOutline(points, strip);
}

export type FreeformStyleKind = "other" | "sofa" | "chair" | "table" | "storage" | "bed" | "rug";

export interface FreeformStyle {
  kind: FreeformStyleKind;
  /** Edge indices that carry a backrest / headboard. */
  backEdges: number[];
  /** Edge indices that carry arms. */
  armEdges: number[];
  /** Edge index of the drawer / door front (storage). */
  frontEdge: number | null;
}

export const DEFAULT_FREEFORM_STYLE: FreeformStyle = {
  kind: "other",
  backEdges: [],
  armEdges: [],
  frontEdge: null,
};

export function frontKindForStyle(kind: FreeformStyleKind): FrontKind {
  switch (kind) {
    case "sofa":
    case "chair":
      return "seat";
    case "bed":
      return "sleep";
    case "storage":
      return "drawer";
    default:
      return "none";
  }
}

/**
 * Build parts for an arbitrary outline (inches, any origin) according to the
 * chosen style. The outline itself is always the `body`; bands along the
 * chosen edges become backrests / arms / fronts; the inset interior becomes
 * the seat / mattress / top.
 */
export function buildFreeformParts(outline: Vec2[], style: FreeformStyle, height: number): FurniturePart[] {
  resetPartIds();
  if (outline.length < 3) return [];
  const bb = boundingBox(outline);
  const minDim = Math.max(1, Math.min(bb.width, bb.height));
  const parts: FurniturePart[] = [];
  const h = Math.max(1, height);
  const validEdge = (i: number) => i >= 0 && i < outline.length;

  switch (style.kind) {
    case "sofa":
    case "chair": {
      const seatH = Math.min(18, h * 0.55);
      const BACK = clamp(minDim * 0.24, 4, 11);
      const ARM = clamp(minDim * 0.16, 3, 9);
      parts.push(part("body", outline, 0, seatH, { cornerRadius: 1 }));
      let interior: Vec2[] | null = insetPolygon(outline, 1);
      for (const e of style.backEdges.filter(validEdge)) {
        const band = edgeBand(outline, e, BACK);
        if (band) {
          parts.push(part("backrest", band, seatH, h, { cornerRadius: 1 }));
          if (interior) interior = differenceOutline(interior, edgeBand(outline, e, BACK + 0.75) ?? band) ?? interior;
        }
      }
      for (const e of style.armEdges.filter(validEdge)) {
        const band = edgeBand(outline, e, ARM);
        if (band) {
          parts.push(part("arm", band, seatH, h * 0.78, { cornerRadius: 1.5 }));
          if (interior) interior = differenceOutline(interior, edgeBand(outline, e, ARM + 0.75) ?? band) ?? interior;
        }
      }
      if (interior && interior.length >= 3) {
        // Split long seats into cushions along the longer axis.
        const ib = boundingBox(interior);
        const along = ib.width >= ib.height ? "x" : "y";
        const length = along === "x" ? ib.width : ib.height;
        const seats = style.kind === "chair" ? 1 : Math.max(1, Math.round(length / 26));
        if (seats > 1) {
          const cells = divide(along === "x" ? ib.minX : ib.minY, length, seats, 1);
          for (const c of cells) {
            const strip =
              along === "x"
                ? rect(c.start, ib.minY - 1, c.size, ib.height + 2)
                : rect(ib.minX - 1, c.start, ib.width + 2, c.size);
            const cushion = intersectionOutline(interior, strip);
            if (cushion) parts.push(part("seat", cushion, seatH, seatH + 4, { cornerRadius: 1.5 }));
          }
        } else {
          parts.push(part("seat", interior, seatH, seatH + 4, { cornerRadius: 1.5 }));
        }
      }
      break;
    }
    case "bed": {
      const frameH = Math.min(h, 14);
      parts.push(part("body", outline, 0, frameH, { cornerRadius: 1 }));
      let mattress: Vec2[] | null = insetPolygon(outline, 1);
      for (const e of style.backEdges.filter(validEdge)) {
        const band = edgeBand(outline, e, 3.5);
        if (band) {
          parts.push(part("headboard", band, 0, h + 20));
          if (mattress) mattress = differenceOutline(mattress, edgeBand(outline, e, 4) ?? band) ?? mattress;
        }
      }
      if (mattress && mattress.length >= 3) {
        parts.push(part("mattress", mattress, frameH, h, { cornerRadius: 2 }));
        // Blanket over the far two-thirds from the headboard (or the front).
        const mb = boundingBox(mattress);
        const blanketStrip = rect(mb.minX - 1, mb.minY + mb.height * 0.38, mb.width + 2, mb.height);
        const blanket = intersectionOutline(mattress, blanketStrip);
        if (blanket) parts.push(part("blanket", blanket, h, h + 1.5, { cornerRadius: 1.5 }));
        // Pillows near the head end.
        const pillowStrip = rect(mb.minX + 2, mb.minY + 2, mb.width - 4, clamp(mb.height * 0.17, 8, 16));
        const pillowArea = intersectionOutline(mattress, pillowStrip);
        if (pillowArea) {
          const pb = boundingBox(pillowArea);
          const n = pb.width >= 50 ? 2 : 1;
          for (const c of divide(pb.minX, pb.width, n, 2)) {
            const pillow = intersectionOutline(pillowArea, rect(c.start, pb.minY, c.size, pb.height));
            if (pillow) parts.push(part("pillow", pillow, h, h + 3, { cornerRadius: 3 }));
          }
        }
      }
      break;
    }
    case "table": {
      parts.push(part("top", outline, h - 1.5, h, { cornerRadius: 0.75 }));
      break;
    }
    case "storage": {
      parts.push(part("body", outline, 0, h, { cornerRadius: 0.5 }));
      if (style.frontEdge != null && validEdge(style.frontEdge)) {
        const band = edgeBand(outline, style.frontEdge, 1.25);
        if (band) parts.push(part("drawer-face", band, 0, h + 0.01));
      }
      break;
    }
    case "rug": {
      parts.push(part("body", outline, 0, 0.5));
      const inner = insetPolygon(outline, clamp(minDim * 0.06, 2, 5));
      if (inner) parts.push(part("cushion", inner, 0.5, 0.55, { cornerRadius: 0.25 }));
      break;
    }
    default:
      parts.push(part("body", outline, 0, h, { cornerRadius: clamp(minDim * 0.06, 0.5, 3) }));
  }
  return parts;
}
